/**
 * Device Management Page
 * Hanterar enhetslivscykel: lista, status, fordonskopplingar, import
 * 
 * Refaktorerad: State och logik flyttat till useDeviceManagement hook
 * Types flyttat till types/device.ts
 * ImportModal flyttat till components/devices/DeviceImportModal.tsx
 */

import React, { useState, useCallback } from 'react';
import { useLanguage, useTranslation } from '@/store/languageStore';
import { Link, useNavigate } from 'react-router-dom';
import {
 ArrowLeftIcon,
 DevicePhoneMobileIcon,
 MagnifyingGlassIcon,
 ArrowPathIcon,
 TruckIcon,
 ChevronRightIcon,
 DocumentArrowUpIcon,
 ExclamationTriangleIcon,
 CheckCircleIcon,
 XCircleIcon,
 ClockIcon,
 ArrowsRightLeftIcon,
 LinkSlashIcon,
 CpuChipIcon,
 BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import SimStatusCard from '@/components/devices/SimStatusCard';
import DeviceObdModePanel from '@/components/devices/DeviceObdModePanel';
import ObdBaselineAdminPanel from '@/components/devices/ObdBaselineAdminPanel';
import DeviceImportModal from '@/components/devices/DeviceImportModal';
import DeviceAssignModal from '@/components/devices/DeviceAssignModal';
import DeviceObsListPanel from '@/components/devices/DeviceObsListPanel';
import HierarchicalOrgSelect from '@/components/common/HierarchicalOrgSelect';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useDeviceManagement } from '@/hooks/useDeviceManagement';
import { statusConfig, changeTypeLabels, DeviceStatus } from '@/types/device';
import { ROUTES } from '@/constants/routes';
import api from '@/services/api/client';
import { useAuthStore } from '@/store/authStore';
import { isSysAdminUser } from '@/utils/isSysAdminUser';

const DeviceManagement: React.FC = () => {
 const navigate = useNavigate();
 const { language } = useLanguage();
 const { t } = useTranslation();
 const { confirm, ConfirmDialog } = useConfirmDialog();
 const isGlobalAdmin = useAuthStore((s) => s.user?.is_global_admin === true);
 const isSysAdmin = isSysAdminUser(useAuthStore((s) => s.user));

 // All state and logic from custom hook
 const {
 devices,
 stats,
 loading,
 selectedDevice,
 deviceHistory,
 historyLoading,
 search,
 setSearch,
 statusFilter,
 setStatusFilter,
 setSelectedDevice,
 showImportModal,
 setShowImportModal,
 handleStatusChange,
 handleUnmount,
 handleAssignOrganization,
 refreshData,
 handleImportSuccess,
 } = useDeviceManagement();

 const [checkedImeis, setCheckedImeis] = useState<Set<string>>(new Set());
 const [showBulkOrgSelect, setShowBulkOrgSelect] = useState(false);
 const [bulkAssigning, setBulkAssigning] = useState(false);
 const [showAssignModal, setShowAssignModal] = useState(false);
 const [obsImeis, setObsImeis] = useState<Set<string>>(new Set());

 const toggleCheck = useCallback((imei: string, e: React.MouseEvent) => {
 e.stopPropagation();
 setCheckedImeis(prev => {
 const next = new Set(prev);
 if (next.has(imei)) next.delete(imei); else next.add(imei);
 return next;
 });
 }, []);

 const toggleCheckAll = useCallback(() => {
 const assignable = devices.filter(d => d.status !== 'VEHICLE' && d.status !== 'DECOMMISSIONED');
 if (checkedImeis.size === assignable.length) {
 setCheckedImeis(new Set());
 } else {
 setCheckedImeis(new Set(assignable.map(d => d.imei)));
 }
 }, [devices, checkedImeis.size]);

 const handleBulkAssign = useCallback(async (orgId: string) => {
 if (!orgId || checkedImeis.size === 0) return;
 setBulkAssigning(true);
 try {
 const res = await api.post('/api/device-management/bulk-assign', {
 imeis: Array.from(checkedImeis),
 organizationId: orgId,
 });
 const data = res.data?.data;
 toast.success(
 `${data?.succeeded?.length || 0} ${t('deviceManagement.devicesAssigned')}`
 );
 setCheckedImeis(new Set());
 setShowBulkOrgSelect(false);
 refreshData();
 } catch (err: any) {
 toast.error(err?.response?.data?.error || err.message);
 } finally {
 setBulkAssigning(false);
 }
 }, [checkedImeis, t, refreshData]);

 return (
 <div className="flex flex-col h-full bg-bgDefault">
 {/* Header */}
 <div className="p-6 border-b border-borderDefault">
 <div className="flex items-center justify-between mb-4">
 <div className="flex items-center gap-3">
 <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
 <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
 </button>
 <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
 <DevicePhoneMobileIcon className="w-6 h-6 text-primary" />
 {t('deviceManagement.title')}
 </h1>
 </div>
 <div className="flex gap-2">
 <Link
 to={ROUTES.ADMIN_DEVICE_AVL_CAPABILITIES}
 className="flex items-center gap-2 px-4 py-2 bg-bgElevated text-textDefault border border-borderDefault rounded-lg hover:bg-bgHover transition-colors"
 >
 <CpuChipIcon className="w-5 h-5" />
 {t('deviceAvlCapabilities.title')}
 </Link>
 <button
 onClick={() => setShowAssignModal(true)}
 className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
 >
 <BuildingOfficeIcon className="w-5 h-5" />
 {t('deviceManagement.importImeiToOrg')}
 </button>
 <button
 onClick={() => setShowImportModal(true)}
 className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
 >
 <DocumentArrowUpIcon className="w-5 h-5" />
 {t('deviceManagement.importReturns')}
 </button>
 <button
 onClick={refreshData}
 className="flex items-center gap-2 px-4 py-2 bg-bgElevated text-textDefault border border-borderDefault rounded-lg hover:bg-bgHover transition-colors"
 >
 <ArrowPathIcon className="w-5 h-5" />
 {t('common.refresh')}
 </button>
 </div>
 </div>
 
 {/* Stats */}
 {stats && (
 <div className="grid grid-cols-6 gap-4 mb-4">
 <div className="bg-bgElevated p-4 rounded-lg border border-borderDefault">
 <div className="text-3xl font-bold text-textDefault">{stats.total}</div>
 <div className="text-sm text-textSecondary">{t('deviceManagement.total')}</div>
 </div>
 {Object.entries(statusConfig).map(([status, config]) => (
 <div key={status} className={`${config.bgColor} p-4 rounded-lg`}>
 <div className={`text-3xl font-bold ${config.color}`}>
 {stats.byStatus[status as DeviceStatus] || 0}
 </div>
 <div className={`text-sm ${config.color}`}>
 {language === 'sv' ? config.label : config.labelEn}
 </div>
 </div>
 ))}
 </div>
 )}
 
 {/* Filters */}
 <div className="flex gap-4">
 <div className="relative flex-1 max-w-md">
 <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-textTertiary" />
 <input
 type="text"
 placeholder={t('deviceManagement.searchPlaceholder')}
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 className="w-full pl-10 pr-4 py-2 bg-bgElevated border border-borderDefault rounded-lg text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 <select
 value={statusFilter}
 onChange={(e) => setStatusFilter(e.target.value as DeviceStatus | '')}
 className="px-4 py-2 bg-bgElevated border border-borderDefault rounded-lg text-textDefault focus:outline-none focus:ring-2 focus:ring-blue-500"
 >
 <option value="">{t('deviceManagement.allStatuses')}</option>
 {Object.entries(statusConfig).map(([status, config]) => (
 <option key={status} value={status}>
 {language === 'sv' ? config.label : config.labelEn}
 </option>
 ))}
 </select>
 </div>
 </div>

 {(isGlobalAdmin || isSysAdmin) && (
 <div className="px-6 pt-4 shrink-0 max-h-80 overflow-y-auto border-b border-borderDefault">
 {isGlobalAdmin && <ObdBaselineAdminPanel />}
 <DeviceObsListPanel
 selectedImei={selectedDevice?.imei}
 selectedReg={selectedDevice?.vehicle_registration}
 onOpenImeisChange={setObsImeis}
 />
 </div>
 )}
 
 {/* Content */}
 <div className="flex flex-1 overflow-hidden">
 {/* Device List */}
 <div className="w-1/2 border-r border-borderDefault overflow-y-auto flex flex-col">
 {/* Bulk assign bar */}
 {checkedImeis.size > 0 && (
 <div className="sticky top-0 z-10 bg-indigo-50 dark:bg-indigo-900/30 border-b border-indigo-200 dark:border-indigo-800 p-3 flex items-center gap-3">
 <span className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
 {checkedImeis.size} {t('deviceManagement.selected')}
 </span>
 {showBulkOrgSelect ? (
 <div className="flex items-center gap-2 flex-1">
 <HierarchicalOrgSelect
 value=""
 onChange={(orgId) => orgId && handleBulkAssign(orgId)}
 placeholder={t('deviceManagement.selectOrganization')}
 showEmptyOption={false}
 disabled={bulkAssigning}
 className="text-sm flex-1"
 />
 <button
 onClick={() => setShowBulkOrgSelect(false)}
 className="text-xs text-textSecondary hover:text-textDefault"
 >
 {t('common.cancel')}
 </button>
 </div>
 ) : (
 <>
 <button
 onClick={() => setShowBulkOrgSelect(true)}
 className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
 >
 <BuildingOfficeIcon className="w-4 h-4 inline mr-1" />
 {t('deviceManagement.assignOrganization')}
 </button>
 <button
 onClick={() => setCheckedImeis(new Set())}
 className="text-xs text-indigo-600 dark:text-indigo-300 hover:underline ml-auto"
 >
 {t('deviceManagement.deselectAll')}
 </button>
 </>
 )}
 </div>
 )}

 {loading ? (
 <div className="flex items-center justify-center h-64">
 <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-500" />
 </div>
 ) : devices.length === 0 ? (
 <div className="flex flex-col items-center justify-center h-64 text-textSecondary">
 <DevicePhoneMobileIcon className="w-16 h-16 mb-4 opacity-50" />
 <p>{t('deviceManagement.noDevices')}</p>
 </div>
 ) : (
 <div className="divide-y divide-borderDefault">
 {/* Select all checkbox */}
 <div className="p-2 px-4 bg-bgElevated border-b border-borderDefault flex items-center gap-2">
 <input
 type="checkbox"
 checked={
 devices.filter(d => d.status !== 'VEHICLE' && d.status !== 'DECOMMISSIONED').length > 0 &&
 checkedImeis.size === devices.filter(d => d.status !== 'VEHICLE' && d.status !== 'DECOMMISSIONED').length
 }
 onChange={toggleCheckAll}
 className="w-4 h-4 rounded border-border text-indigo-600 focus:ring-indigo-500"
 />
 <span className="text-xs text-textSecondary">
 {t('deviceManagement.selectAllAvailable')}
 </span>
 </div>
 {devices.map((device) => {
 const canCheck = device.status !== 'VEHICLE' && device.status !== 'DECOMMISSIONED';
 return (
 <div
 key={device.id}
 onClick={() => setSelectedDevice(device)}
 className={`p-4 cursor-pointer hover:bg-bgHover transition-colors ${
 selectedDevice?.id === device.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
 }`}
 >
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3 flex-1">
 {canCheck && (
 <input
 type="checkbox"
 checked={checkedImeis.has(device.imei)}
 onClick={(e) => toggleCheck(device.imei, e)}
 onChange={() => {}}
 className="w-4 h-4 rounded border-border text-indigo-600 focus:ring-indigo-500 shrink-0"
 />
 )}
 {!canCheck && <div className="w-4 shrink-0" />}
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <span className="font-mono font-medium text-textDefault">{device.imei}</span>
 <span className={`px-2 py-0.5 text-xs rounded-full ${statusConfig[device.status].bgColor} ${statusConfig[device.status].color}`}>
 {language === 'sv' ? statusConfig[device.status].label : statusConfig[device.status].labelEn}
 </span>
 {obsImeis.has(device.imei) && (
 <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
 {t('deviceManagement.obsBadge')}
 </span>
 )}
 </div>
 <div className="flex items-center gap-4 mt-1 text-sm text-textSecondary">
 {device.vehicle_registration && (
 <span className="flex items-center gap-1">
 <TruckIcon className="w-4 h-4" />
 {device.vehicle_registration}
 </span>
 )}
 {device.organization_name && (
 <span>{device.organization_name}</span>
 )}
 </div>
 </div>
 </div>
 <ChevronRightIcon className="w-5 h-5 text-textTertiary" />
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 
 {/* Device Details */}
 <div className="w-1/2 overflow-y-auto">
 {selectedDevice ? (
 <div className="p-6">
 {/* Device Info */}
 <div className="mb-6">
 <h2 className="text-xl font-bold text-textDefault mb-4">
 {t('deviceManagement.deviceDetails')}
 </h2>
 <div className="bg-bgElevated rounded-lg border border-borderDefault p-4 space-y-3">
 <div className="flex justify-between">
 <span className="text-textSecondary">IMEI</span>
 <span className="font-mono text-textDefault">{selectedDevice.imei}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-textSecondary">Status</span>
 <span className={`px-2 py-0.5 text-sm rounded-full ${statusConfig[selectedDevice.status].bgColor} ${statusConfig[selectedDevice.status].color}`}>
 {language === 'sv' ? statusConfig[selectedDevice.status].label : statusConfig[selectedDevice.status].labelEn}
 </span>
 </div>
 {selectedDevice.iccid && (
 <div className="flex justify-between">
 <span className="text-textSecondary">ICCID</span>
 <span className="font-mono text-sm text-textDefault">{selectedDevice.iccid}</span>
 </div>
 )}
 {selectedDevice.vehicle_registration && (
 <div className="flex justify-between">
 <span className="text-textSecondary">{t('deviceManagement.vehicle')}</span>
 <span className="text-textDefault">
 {selectedDevice.vehicle_registration} ({selectedDevice.vehicle_brand} {selectedDevice.vehicle_model})
 </span>
 </div>
 )}
                <div className="flex justify-between items-start gap-2">
                <span className="text-textSecondary shrink-0">{t('deviceManagement.organization')}</span>
                <div className="min-w-0 flex-1">
                  <HierarchicalOrgSelect
                    value={selectedDevice.organization_id || ''}
                    onChange={(orgId) => orgId && handleAssignOrganization(selectedDevice.imei, orgId)}
                    placeholder={t('deviceManagement.selectOrganization')}
                    showEmptyOption={false}
                    disabled={false}
                    className="text-sm"
                  />
                  {selectedDevice.vehicle_id && (
                    <span className="block text-xs text-textTertiary mt-1">
                      {t('deviceManagement.organizationFollowsVehicle')}
                    </span>
                  )}
                </div>
                </div>
 {selectedDevice.last_known_vin && (
 <div className="flex justify-between">
 <span className="text-textSecondary">{t('deviceManagement.lastKnownVin')}</span>
 <span className="font-mono text-sm text-textDefault">{selectedDevice.last_known_vin}</span>
 </div>
 )}
 </div>
 </div>

 {/* SIM Status */}
 {selectedDevice.iccid && (
 <div className="mb-6">
 <SimStatusCard
 imei={selectedDevice.imei}
 iccid={selectedDevice.iccid}
 language={language}
 onStatusChange={refreshData}
 />
 </div>
 )}

 {/* OBD II Feature */}
 <DeviceObdModePanel
 imei={selectedDevice.imei}
 registrationNumber={selectedDevice.vehicle_registration}
 />

 {/* Actions */}
 <div className="mb-6">
 <h3 className="text-lg font-semibold text-textDefault mb-3">
 {t('deviceManagement.actions')}
 </h3>
 <div className="flex flex-wrap gap-2">
 {selectedDevice.status !== 'STOCK' && (
 <button
 onClick={() => handleStatusChange(selectedDevice.imei, 'STOCK')}
 className="flex items-center gap-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
 >
 <CheckCircleIcon className="w-4 h-4" />
 {t('deviceManagement.toStock')}
 </button>
 )}
 {selectedDevice.vehicle_id && (
 <>
 <button
 onClick={() => handleUnmount(selectedDevice.imei, true)}
 className="flex items-center gap-2 px-3 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
 >
 <LinkSlashIcon className="w-4 h-4" />
 {t('deviceManagement.unmountKeep')}
 </button>
 <button
 onClick={() => handleUnmount(selectedDevice.imei, false)}
 className="flex items-center gap-2 px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
 >
 <ArrowsRightLeftIcon className="w-4 h-4" />
 {t('deviceManagement.unmountStock')}
 </button>
 </>
 )}
 {selectedDevice.status !== 'LOST' && selectedDevice.status !== 'DECOMMISSIONED' && (
 <button
 onClick={() => handleStatusChange(selectedDevice.imei, 'LOST')}
 className="flex items-center gap-2 px-3 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
 >
 <ExclamationTriangleIcon className="w-4 h-4" />
 {t('deviceManagement.markLost')}
 </button>
 )}
 {selectedDevice.status !== 'DECOMMISSIONED' && (
 <button
 onClick={() =>
 confirm({
 title: t('common.confirm') ?? 'Bekräfta',
 message: t('devices.confirmDecommission') ?? 'Är du säker? Detta kan inte ångras.',
 onConfirm: () => handleStatusChange(selectedDevice.imei, 'DECOMMISSIONED'),
 variant: 'danger',
 })
 }
 className="flex items-center gap-2 px-3 py-2 bg-bgBase text-textSecondary rounded-lg hover:bg-bgBase transition-colors"
 >
 <XCircleIcon className="w-4 h-4" />
 {t('deviceManagement.decommission')}
 </button>
 )}
 </div>
 </div>
 
 {/* History */}
 <div>
 <h3 className="text-lg font-semibold text-textDefault mb-3">
 {t('deviceManagement.changeHistory')}
 </h3>
 {historyLoading ? (
 <div className="flex items-center justify-center py-8">
 <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-500" />
 </div>
 ) : deviceHistory.length === 0 ? (
 <div className="text-center py-8 text-textSecondary">
 {t('deviceManagement.noHistory')}
 </div>
 ) : (
 <div className="space-y-2">
 {deviceHistory.map((entry) => (
 <div
 key={entry.id}
 className="bg-bgElevated rounded-lg border border-borderDefault p-3"
 >
 <div className="flex items-start justify-between">
 <div>
 <div className="font-medium text-textDefault">
 {changeTypeLabels[entry.change_type]?.[language === 'sv' ? 'sv' : 'en'] || entry.change_type}
 </div>
 {entry.notes && (
 <div className="text-sm text-textSecondary mt-1">{entry.notes}</div>
 )}
 {entry.new_value && (
 <div className="text-xs text-textTertiary mt-1 font-mono">
 {JSON.stringify(entry.new_value)}
 </div>
 )}
 </div>
 <div className="text-right">
 <div className="text-xs text-textTertiary flex items-center gap-1">
 <ClockIcon className="w-3 h-3" />
 {new Date(entry.created_at).toLocaleString(language === 'sv' ? 'sv-SE' : 'en-GB')}
 </div>
 {entry.performed_by_name && (
 <div className="text-xs text-textSecondary">{entry.performed_by_name}</div>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </div>
 ) : (
 <div className="flex flex-col items-center justify-center h-full text-textSecondary">
 <DevicePhoneMobileIcon className="w-16 h-16 mb-4 opacity-50" />
 <p>{t('deviceManagement.selectDevice')}</p>
 </div>
 )}
 </div>
 </div>
 
 {/* Import Modal */}
 {showImportModal && (
 <DeviceImportModal
 onClose={() => setShowImportModal(false)}
 onSuccess={handleImportSuccess}
 language={language}
 />
 )}

 {/* Assign to Organization Modal */}
 {showAssignModal && (
 <DeviceAssignModal
 onClose={() => setShowAssignModal(false)}
 onSuccess={() => { setShowAssignModal(false); refreshData(); }}
 />
 )}

 <ConfirmDialog />
 </div>
 );
};

export default DeviceManagement;
